import { Controller } from "@hotwired/stimulus"

// Hands an API token straight to the ProjectTruck browser extension so the user
// never has to copy-paste one. Requires the extension to list this origin under
// `externally_connectable` in its manifest.
//
// Falls back to revealing the token for manual entry when the extension isn't
// detected — other browsers, or an install whose ID doesn't match ours.
export default class extends Controller {
  static targets = ["button", "status", "notDetected", "manual", "tokenOutput"]
  static values = { extensionId: String, tokenUrl: String }

  connectExtension() {
    this.setStatus("Looking for the extension…")
    this.disableButton(true)

    this.pingExtension()
      .then((detected) => {
        if (!detected) {
          this.notDetectedTarget.hidden = false
          this.setStatus("Extension not detected in this browser.", "err")
          this.disableButton(false)
          return
        }

        return this.mintToken()
          .then((config) => this.sendConfig(config))
          .then(() => {
            this.notDetectedTarget.hidden = true
            this.setStatus("Connected. Click the toolbar icon on any listing page.", "ok")
            this.buttonTarget.textContent = "Reconnect extension"
            this.disableButton(false)
          })
      })
      .catch((error) => {
        this.setStatus(error.message, "err")
        this.disableButton(false)
      })
  }

  // Escape hatch for browsers we can't message — mint the token and show it so
  // it can be pasted into the extension's options page by hand.
  revealToken() {
    this.setStatus("Issuing a token…")

    this.mintToken()
      .then(({ token }) => {
        this.tokenOutputTarget.textContent = token
        this.manualTarget.hidden = false
        this.setStatus("Paste this into the extension's options page.", "ok")
      })
      .catch((error) => this.setStatus(error.message, "err"))
  }

  // Resolves false rather than rejecting: "no extension here" is an expected
  // outcome, not an error.
  pingExtension() {
    return new Promise((resolve) => {
      if (!window.chrome?.runtime?.sendMessage) {
        resolve(false)
        return
      }

      try {
        chrome.runtime.sendMessage(this.extensionIdValue, { type: "ping" }, (response) => {
          // Reading lastError is what suppresses Chrome's console warning when
          // nothing is listening on that extension ID.
          resolve(!chrome.runtime.lastError && response?.ok === true)
        })
      } catch {
        resolve(false)
      }
    })
  }

  async mintToken() {
    const response = await fetch(this.tokenUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content ?? "",
        "Accept": "application/json"
      }
    })

    if (!response.ok) throw new Error(`Could not issue a token (${response.status}). Try reloading the page.`)

    return response.json()
  }

  // The extension picks its host up from the message sender's origin, so
  // connecting from staging or localhost points it at the right place for free.
  sendConfig({ token }) {
    return new Promise((resolve, reject) => {
      chrome.runtime.sendMessage(this.extensionIdValue, { type: "setConfig", token }, (response) => {
        if (chrome.runtime.lastError || !response?.ok) {
          reject(new Error("The extension refused the token. Reload it from chrome://extensions and try again."))
        } else {
          resolve()
        }
      })
    })
  }

  setStatus(message, kind) {
    this.statusTarget.textContent = message
    this.statusTarget.dataset.state = kind ?? ""
  }

  disableButton(disabled) {
    this.buttonTarget.disabled = disabled
    this.buttonTarget.style.opacity = disabled ? "0.6" : ""
  }
}
